import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import fuzzysort from 'fuzzysort';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { FormGroup, Input, Label } from 'reactstrap';
import { ReduxState } from 'types';
import { useAuthProvider } from 'utils/api/useAuthProvider';
import { withSelfRecoveryErrorBoundary } from 'views/Error/ErrorBoundary';
import * as consts from '../../../consts/consts';
import ElasticsearchAPI from '../../../utils/api/ElasticsearchAPI';
import { capitalize, getUniqueHtmlId } from '../../../utils/util/formatter';
import { isEnabled } from '../../../utils/util/util';
import ReactTags from '../../../vendor/react-tags-6.0/lib/ReactTags';
import { renderSuggestion } from './ValidatedAutosuggest';

const MAX_RECOMMENDATIONS_LENGTH = 7;

// per research from QA, the breaking point between legit and "long sentences"
// based on the data of skills analyzed (as of August 2022) for input is about
// 40 characters. Also, 4 spaces, even if shorter, is another sign that this
// is a sentence instead of a valid skill.
const MAX_LENGTH_OF_INPUT = 40;
const MAX_SPACES_IN_INPUT = 4;

const ReactTagsWithErrorBoundary = withSelfRecoveryErrorBoundary(ReactTags);

const DEFAULT_TAGS_ARE_EQUAL = (a, b) => {
  // match if object exists and ids match
  if (a?.object?.id && b?.object?.id && a.object.id === b.object.id) {
    return true;
  }

  // match if names match (case-insensitive)
  return a.name && b.name && a.name.toLowerCase() === b.name.toLowerCase();
};

const DEFAULT_TAG_MATCH_FUNCTION = (x) => x;
const DEFAULT_RENDER_CREATE_OPTION = (x) => ({ name: x });
const DEFAULT_VALUE = [];
const DEFAULT_SUGGESTIONS = [];
const DEFAULT_RECCOMENDATIONS = [];
const DEFAULT_DELIMITERS = [';', ',', 'Enter', 'Tab'];
const DEFAULT_EXCLUDE_LIST = [];

const ReactTagsInput: FC<Props> = ({
  tagsAreEqual: propsTagsAreEqual = DEFAULT_TAGS_ARE_EQUAL,
  excludeTagMatchFunction:
    propsExcludeTagMatchFunction = DEFAULT_TAG_MATCH_FUNCTION,
  renderCreateOption = DEFAULT_RENDER_CREATE_OPTION,
  allowDuplicateCreate = false,
  allowNew = true,
  allowNewOnlyIfNoSuggestions = false,
  disabled: propsIsDisabled = false,
  value = DEFAULT_VALUE,
  suggestions: propsSuggestions = DEFAULT_SUGGESTIONS,
  recommendations = DEFAULT_RECCOMENDATIONS,
  delimiters = DEFAULT_DELIMITERS,
  useTagCards = false,
  excludeList: propsExcludeList = DEFAULT_EXCLUDE_LIST,
  disableLengthCheck = false,
  maxTags: propsMaxTags = 1000,
  propagateOnClearInput = false,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const [suggestions, setSuggestions] = useState(
    propsSuggestions ? propsSuggestions : []
  );
  const [defaultOption, setDefaultOption] = useState(null);
  // NOTE: needs to be undefined instead of null so no type is passed
  // into backend when type is not relevant
  const [defaultOptionType, setDefaultOptionType] = useState(undefined);
  const [tags, setTags] = useState(value);
  const [elasticsearchSuggestions, setElasticsearchSuggestions] = useState([]);
  const [defaultOptionIndex, setDefaultOptionIndex] = useState(undefined);
  const propsPlaceholder =
    props.placeholder ??
    formatMessage({
      id: 'app.views.widgets.inputs.react_tags_input.placeholder',
      defaultMessage: 'Short keywords only',
    });
  const [placeholderText, setPlaceholderText] = useState(
    // @ts-expect-error
    value?.length > 0 && !props.showPlaceholderAfterFirstTag
      ? ''
      : propsPlaceholder
  );
  const { user } = useAuthProvider();
  const [isInputTooLong, setIsInputTooLong] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [inputQuery, setInputQuery] = useState('');

  const defaultInputRef = useRef();
  const [inputRef, setInputRef] = useState(
    props.innerRef ? props.innerRef : defaultInputRef
  );

  const [validationMessage, setValidationMessage] = useState(null);
  const propsOnValidChange = props.onValidChange;
  const propsRequired = props.required;
  const suggestionsTransform = props.suggestionsTransform;
  const propsCallback = props.callback;
  const propsOnBeforeChange = props.onBeforeChange;
  const propsOnInputChange = props.onInputChange;
  const currentOrgId = props.currentOrganization?.id;
  const esOptions = props.elasticsearchOptions;
  const userSub = user?.sub;
  const validationFunction = props.validationFunction;
  const propsExcludeListErrorMessage =
    props.excludeListErrorMessage ??
    formatMessage({
      id: 'app.views.widgets.inputs.react_tags_input.is_already_on_the_list',
      defaultMessage: 'is already on the list.',
    });
  const propsExcludeListErrorMessageFunction =
    props.excludeListErrorMessageFunction;

  const intl = useIntl();

  useEffect(() => {
    // if there are any invalid tags, require their removal first
    // @ts-expect-error
    if (tags?.length > 0 && validationFunction) {
      // @ts-expect-error
      const invalidTags = tags.filter((t) => !validationFunction(t.name, true));
      if (invalidTags.length > 0) {
        setValidationMessage(
          // @ts-expect-error
          formatMessage(
            {
              id: 'app.views.widgets.inputs.react_tags_input.remove_invalid_tags',
              defaultMessage: 'Remove invalid tags: {tags}',
            },
            {
              // @ts-expect-error
              tags: invalidTags.map((t) => t.name).join(', '),
            }
          )
        );
        return;
      }
    }

    if (propsRequired) {
      // @ts-expect-error
      const isValid = tags?.length > 0;
      setValidationMessage(
        // @ts-expect-error
        isValid
          ? null
          : formatMessage({
              id: 'app.views.widgets.inputs.react_tags_input.required',
              defaultMessage: 'This field is required.',
            })
      );
    } else {
      setValidationMessage(null);
    }
  }, [propsRequired, tags, validationFunction, formatMessage]);

  useEffect(() => {
    if (propsOnValidChange) {
      propsOnValidChange(validationMessage);
    }
  }, [validationMessage, propsOnValidChange]);

  useEffect(() => {
    setInputRef(props.innerRef ? props.innerRef : defaultInputRef);
  }, [props.innerRef]);

  const tagComponent = useCallback(
    ({ tag, removeButtonText, onDelete }) => {
      return renderSuggestion(
        intl,
        tag,
        '',
        true,
        'xxs',
        false,
        removeButtonText,
        onDelete,
        true,
        false,
        false,
        // @ts-expect-error
        validationFunction,
        propsIsDisabled
      );
    },
    [validationFunction, propsIsDisabled, intl]
  );

  // calculate placeholder in useLayoutEffect as size of input is computed based on
  // length of this field
  useLayoutEffect(() => {
    // update ui if tag length changes to ensure placeholder width gets recalculated
    setPlaceholderText(
      // @ts-expect-error
      tags?.length > 0 && !props.showPlaceholderAfterFirstTag
        ? ''
        : propsPlaceholder
    );
  }, [props.showPlaceholderAfterFirstTag, propsPlaceholder, tags]);

  useEffect(() => {
    setTags(value);
  }, [value]);

  // Gets exact match from suggestions (only needed for non elastic search querying)
  const exactSuggestions = useMemo(() => {
    if (!propsSuggestions) {
      return [];
    }

    return fuzzysort
      .go(inputQuery, propsSuggestions, {
        key: 'name',
      })
      .map((s) => s.obj);
  }, [inputQuery, propsSuggestions]);

  useEffect(() => {
    setSuggestions(
      exactSuggestions
        ? exactSuggestions.concat(elasticsearchSuggestions)
        : elasticsearchSuggestions
    );
  }, [exactSuggestions, elasticsearchSuggestions]);

  const suggestionComponentFunction = useCallback(
    ({ item, query }) => {
      // show dropdown items in size 'xs' for larger selection size, Fitt's law (although tags are size 'xxs')
      const content = renderSuggestion(intl, item, query, true, 'xs');
      // Generic incompatibility surfacing: any backend that marks a hit
      // with `incompatibility: {reason}` gets a chip rendered alongside
      // the row. Selection blocking comes from `item.disabled` (set in
      // ElasticsearchAPI.defaultSelectorMapFunction), which the vendored
      // react-tags library honors natively.
      const reason = item?.incompatibility?.reason;
      if (!reason) return content;
      return (
        <div>
          {content}
          <div className="small mt-1">{reason}</div>
        </div>
      );
    },
    [intl]
  );

  const onDelete = useCallback(
    (i) => {
      // Do not propagate if the tag list is already empty
      if (i === -1) {
        return;
      }
      // cancel if disallowed
      // @ts-expect-error
      if (propsOnBeforeChange && !propsOnBeforeChange('delete', tags[i], tags))
        return;

      // @ts-expect-error
      const newTags = tags.slice(0);

      // TODO: find way to prepend input field with newTags[i] to be more forgiving of deletion,
      // i.e. if someone doesn't realize delete removes the entire tag

      newTags.splice(i, 1);
      setTags(newTags);

      if (typeof propsCallback !== 'undefined') {
        // @ts-expect-error

        propsCallback(newTags);
      }
    },
    [propsCallback, propsOnBeforeChange, tags]
  );

  const propsClearTagsAfterCallback = props.clearTagsAfterCallback;
  const onAddition = useCallback(
    (tag) => {
      // cancel if disallowed
      if (propsOnBeforeChange && !propsOnBeforeChange('add', tag, tags)) return;

      // if they select something long on the list,
      // the notice should go away
      setIsInputTooLong(false);

      // if tag is default option, apply index if it is set
      // (this is for, example, when we have buttons to indicate
      // whether the new item is a skill or credential, or for
      // a skill of a specific type, e.g. behavior)
      if (
        (defaultOptionIndex || defaultOptionType) &&
        defaultOption &&
        defaultOption === tag
      ) {
        tag = {
          name: tag.name,
          type: defaultOptionType, // needed for skill types (e.g. create behavior, not experience)
          _index: defaultOptionIndex,
          object: {
            name: tag.name,
            type: defaultOptionType, // needed for skill types (e.g. create behavior, not experience)
            _index: defaultOptionIndex,
          },
        };
      }

      // strip whitespace from ends of tag
      tag.name = typeof tag.name === 'string' ? tag.name.trim() : tag.name;

      // tag either has object or not; equality should be tested accordingly
      // if tag object is on exclude list, ignore it and tell the user
      if (
        (propsExcludeList &&
          propsExcludeList.findIndex((excludeTag) =>
            // @ts-expect-error
            propsTagsAreEqual(excludeTag, propsExcludeTagMatchFunction(tag))
          ) !== -1) ||
        (tags &&
          tags
            // @ts-expect-error
            .map(propsExcludeTagMatchFunction)
            .findIndex((existingTag) =>
              // @ts-expect-error
              propsTagsAreEqual(existingTag, propsExcludeTagMatchFunction(tag))
            ) !== -1)
      ) {
        if (propsExcludeListErrorMessageFunction) {
          toast.info(propsExcludeListErrorMessageFunction(tag));
        } else {
          toast.info(capitalize(tag.name) + ' ' + propsExcludeListErrorMessage);
        }
        return;
      }

      // @ts-expect-error
      const newTags = [...tags, tag];

      if (!propsClearTagsAfterCallback) {
        setTags(newTags);
      }

      if (typeof propsCallback !== 'undefined') {
        // @ts-expect-error
        propsCallback(newTags);
      }
    },
    [
      defaultOptionIndex,
      defaultOptionType,
      defaultOption,
      propsExcludeList,
      tags,
      propsExcludeTagMatchFunction,
      propsClearTagsAfterCallback,
      propsCallback,
      propsOnBeforeChange,
      propsTagsAreEqual,
      propsExcludeListErrorMessageFunction,
      propsExcludeListErrorMessage,
    ]
  );

  const onInputChange = useCallback(
    (query) => {
      // if this is just spaces, ignore it
      if (!query?.trim()) {
        if (props.deliverEmptyInputChange && propsOnInputChange) {
          propsOnInputChange(query);
        }
        return;
      }

      if (disableLengthCheck) {
        setIsInputTooLong(false);
      } else {
        const spaceCount = (query.trim().match(/ /g) || []).length;
        const tooLong =
          query.length > MAX_LENGTH_OF_INPUT ||
          spaceCount >= MAX_SPACES_IN_INPUT;

        setIsInputTooLong(tooLong);

        // do not allow the creation of these long skills at all
        // (but still allow selecting them in a dropdown, e.g.
        // for long acronyms w/descriptions that are curated by us)
        if (tooLong) {
          return;
        }
      }

      if (esOptions && query) {
        // @ts-expect-error
        const q = esOptions.getQuery
          ? // @ts-expect-error
            esOptions.getQuery(query)
          : query
            ? { query: query }
            : {};

        // restrict results to active employees
        if (isEnabled(props.features, consts.FLAGS.HIDE_FORMER_EMPLOYEES)) {
          q.person_statuses = ['A', 'L'];
        }

        // @ts-expect-error
        if (esOptions.queryParameters) {
          // @ts-expect-error
          Object.keys(esOptions.queryParameters).forEach((key) => {
            // @ts-expect-error
            q[key] = esOptions.queryParameters[key];
          });
        }

        ElasticsearchAPI.search(
          // only cache if no query provided (so we don't take up too much
          // memory story search results as searches are done)
          q && Object.keys(q).length > 0 ? undefined : userSub,
          props.currentProxyPerson,
          currentOrgId,
          // @ts-expect-error
          esOptions.url,
          q,
          (hits) => {
            setElasticsearchSuggestions(
              hits.map(
                // @ts-expect-error
                esOptions.mapFunction
                  ? // @ts-expect-error
                    esOptions.mapFunction
                  : ElasticsearchAPI.defaultSelectorMapFunction
              )
            );
          },
          (error) => {
            console.error('ReactTagsInput error: ' + JSON.stringify(error));
            setElasticsearchSuggestions([]);
          }
        );
      }

      if (query) {
        setDefaultOption(
          // @ts-expect-error
          renderCreateOption(query, setDefaultOptionIndex, setDefaultOptionType)
        );
      } else {
        setDefaultOption(null);
      }

      setInputQuery(query);

      if (propsOnInputChange) {
        return propsOnInputChange(query);
      }
    },
    [
      disableLengthCheck,
      props.deliverEmptyInputChange,
      props.currentProxyPerson,
      esOptions,
      propsOnInputChange,
      userSub,
      currentOrgId,
      renderCreateOption,
      props.features,
    ]
  );

  const debouncedOnInputChange = useMemo(
    () =>
      debounce(onInputChange, 250, {
        leading: false,
      }),
    [onInputChange]
  );

  const inputAttributes = useMemo(() => {
    const attrs = {
      spellCheck: false,
      autoComplete: props.autoComplete ? props.autoComplete : 'off', // so extensions like 1password don't show up
      name: props.name,
      // @ts-expect-error
      readOnly: tags.length >= propsMaxTags,
    };

    if (propsIsDisabled) {
      // @ts-expect-error
      attrs.disabled = true;
    }

    return attrs;
  }, [
    props.autoComplete,
    props.name,
    propsIsDisabled,
    propsMaxTags,
    // @ts-expect-error
    tags.length,
  ]);

  const reactTagsSuggestions = useMemo(() => {
    let finalSuggestions = suggestionsTransform
      ? suggestionsTransform(suggestions)
      : suggestions;

    if (allowNewOnlyIfNoSuggestions) {
      if (defaultOption && !(finalSuggestions?.length > 0)) {
        finalSuggestions = [defaultOption];
      }
    } else if (allowNew) {
      if (defaultOption) {
        // If allowDuplicateCreate is enabled, will allow creation of
        // a suggestion with a duplicate name. Otherwise, if default option
        // doesn't exact-match an existing suggestion, expose it in list
        if (
          finalSuggestions &&
          (allowDuplicateCreate ||
            finalSuggestions.findIndex((suggestion) =>
              // @ts-expect-error
              propsTagsAreEqual(
                suggestion,
                // @ts-expect-error
                propsExcludeTagMatchFunction(defaultOption)
              )
            ) === -1)
        ) {
          finalSuggestions = finalSuggestions.concat(defaultOption);
        }
      }
    }

    return finalSuggestions;
  }, [
    defaultOption,
    allowNew,
    allowNewOnlyIfNoSuggestions,
    allowDuplicateCreate,
    propsExcludeTagMatchFunction,
    propsTagsAreEqual,
    suggestions,
    suggestionsTransform,
  ]);

  const filteredRecommendations = useMemo(
    () =>
      recommendations
        ?.filter(
          // @ts-expect-error
          (i) => tags && tags.findIndex((t) => propsTagsAreEqual(t, i)) === -1
        )
        .slice(0, MAX_RECOMMENDATIONS_LENGTH),
    [recommendations, propsTagsAreEqual, tags]
  );

  const addTag = useCallback(
    // @ts-expect-error
    (tag) => inputRef.current?.addTag(tag),
    [inputRef]
  );

  // detect blurring of input while not registering false blurs when people
  // click on tags to remove them
  const BLUR_DELAY = 200; // milliseconds
  const [blurredAt, setBlurredAt] = useState();
  const onBlur = useCallback(() => {
    // if they select something long on the list,
    // the notice should go away
    setIsInputTooLong(false);

    if (props.onBlur) {
      setBlurredAt(
        // @ts-expect-error
        setTimeout(() => {
          // @ts-expect-error
          props.onBlur();
        }, BLUR_DELAY)
      );
    }
  }, [props]);
  const onFocus = useCallback(() => {
    if (blurredAt) {
      clearTimeout(blurredAt);
    }
    setBlurredAt(undefined);
  }, [blurredAt]);

  const reactTagsInputSpanId = useMemo(() => getUniqueHtmlId(), []);

  const inputElement = useMemo(
    () =>
      props.readOnly ? (
        <Input
          type="textarea"
          innerRef={inputRef}
          disabled
          // @ts-expect-error
          value={tags.map((tag) => tag.name).join(', ')}
        />
      ) : (
        <>
          <span
            className={propsIsDisabled ? 'disabled' : undefined}
            onFocus={() => setHasFocus(true)}
            onBlur={() => setHasFocus(false)}
            id={reactTagsInputSpanId}
          >
            <ReactTagsWithErrorBoundary
              classNames={
                props.className || props.inputClassName
                  ? {
                      root:
                        'react-tags' +
                        (props.className ? ' ' + props.className : ''),
                      // keep the rest the same
                      rootFocused: 'is-focused',
                      selected: 'react-tags__selected',
                      selectedTag: 'react-tags__selected-tag',
                      selectedTagName: 'react-tags__selected-tag-name',
                      search:
                        'react-tags__search' +
                        (props.inputClassName
                          ? ' ' + props.inputClassName
                          : '') +
                        // @ts-expect-error
                        (!(tags?.length > 0) ? ' is-empty' : ''),
                      searchWrapper: 'react-tags__search-wrapper',
                      searchInput: 'react-tags__search-input',
                      suggestions: 'react-tags__suggestions',
                      suggestionActive: 'is-active',
                      suggestionDisabled: 'is-disabled',
                      suggestionPrefix: 'react-tags__suggestion-prefix',
                    }
                  : undefined
              }
              inputAttributes={inputAttributes}
              // @ts-expect-error
              ref={inputRef}
              tags={tags}
              onBlur={onBlur}
              onFocus={onFocus}
              onDelete={onDelete}
              onAddition={onAddition}
              delimiters={delimiters}
              // NOTE: allow new is set to FALSE ALWAYS because we add a custom "Create X" item
              // in the suggestion list if allowNew is true; this is to ensure that, for example,
              // typing a comma or tab selects the first item, but that there is still a way
              // to create something new in that case
              allowNew={false}
              addOnBlur={true}
              // we want to withhold autofocus until component
              // is enabled so after being disabled and later
              // enabled it can take focus
              autoFocus={!propsIsDisabled && props.autoFocus}
              disabled={propsIsDisabled}
              placeholderText={placeholderText}
              suggestions={reactTagsSuggestions}
              tagComponent={useTagCards ? tagComponent : undefined}
              suggestionComponent={suggestionComponentFunction}
              suggestionsFilter={props.suggestionsFilter}
              noSuggestionsText={props.noSuggestionsText}
              onInput={debouncedOnInputChange}
              minQueryLength={props.minQueryLength}
              maxSuggestionsLength={props.maxSuggestionsLength}
              propagateOnClearInput={propagateOnClearInput}
              removeButtonText={formatMessage({
                id: 'app.views.widgets.inputs.react_tags_input.remove_tag',
                defaultMessage: 'Click to remove tag',
              })}
            />
          </span>
          {!disableLengthCheck && (
            <UncontrolledPopover
              // don't get in the way of dropdown options that show
              placement="bottom-end"
              isOpen={isInputTooLong && hasFocus}
              target={reactTagsInputSpanId}
            >
              <b>
                <FormattedMessage
                  id="app.views.widgets.inputs.react_tags_input.only_short_keywords"
                  defaultMessage="Only short keywords, please!"
                />
              </b>
              <br />
              <FormattedMessage
                id="app.views.widgets.inputs.react_tags_input.this_will_show_up"
                defaultMessage="
              This will show up as an option for others to select, so please
              keep it short and reusable.
            "
              />
            </UncontrolledPopover>
          )}
          {/* @ts-expect-error */}
          {filteredRecommendations?.length > 0 && (
            <div className="small mt-2">
              <FormattedMessage
                id="app.views.widgets.inputs.react_tags_input.suggested"
                defaultMessage="Suggested:"
              />{' '}
              {/* @ts-expect-error */}
              {filteredRecommendations.map((tag, index) => (
                <Fragment key={index}>
                  {index === 0 ? '' : ', '}
                  <span
                    className="btn-link"
                    role="button"
                    onClick={() => addTag(tag)}
                  >
                    {/* @ts-expect-error */}
                    {tag.name}
                  </span>
                </Fragment>
              ))}
            </div>
          )}
        </>
      ),
    [
      props.readOnly,
      props.className,
      props.inputClassName,
      delimiters,
      props.autoFocus,
      useTagCards,
      props.suggestionsFilter,
      props.noSuggestionsText,
      props.minQueryLength,
      props.maxSuggestionsLength,
      propagateOnClearInput,
      disableLengthCheck,
      inputRef,
      tags,
      propsIsDisabled,
      reactTagsInputSpanId,
      inputAttributes,
      onBlur,
      onFocus,
      onDelete,
      onAddition,
      placeholderText,
      reactTagsSuggestions,
      tagComponent,
      suggestionComponentFunction,
      debouncedOnInputChange,
      formatMessage,
      isInputTooLong,
      hasFocus,
      filteredRecommendations,
      addTag,
    ]
  );

  if (props.label) {
    return (
      <FormGroup>
        <Label>{props.label}</Label>
        {inputElement}
      </FormGroup>
    );
  } else {
    return inputElement;
  }
};

const ReactTagsInput_propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  tagsAreEqual: PropTypes.func,
  name: PropTypes.string,
  autoFocus: PropTypes.bool,
  required: PropTypes.bool,
  innerRef: PropTypes.object,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  value: PropTypes.arrayOf(PropTypes.object),
  allowDuplicateCreate: PropTypes.bool,
  allowNew: PropTypes.bool,
  allowNewOnlyIfNoSuggestions: PropTypes.bool,
  suggestions: PropTypes.arrayOf(PropTypes.object),
  suggestionsFilter: PropTypes.func,
  suggestionsTransform: PropTypes.func,
  recommendations: PropTypes.arrayOf(PropTypes.object),
  delimiters: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  callback: PropTypes.func,
  noSuggestionsText: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  onInputChange: PropTypes.func,
  onBlur: PropTypes.func,
  onValidChange: PropTypes.func,
  minQueryLength: PropTypes.number,
  elasticsearchOptions: PropTypes.object,
  currentOrganization: PropTypes.object.isRequired,
  useTagCards: PropTypes.bool,
  validationFunction: PropTypes.func,
  excludeList: PropTypes.arrayOf(PropTypes.object),
  excludeListErrorMessage: PropTypes.string,
  excludeListErrorMessageFunction: PropTypes.func,
  excludeTagMatchFunction: PropTypes.func,
  renderCreateOption: PropTypes.func,
  showPlaceholderAfterFirstTag: PropTypes.bool,
  autoComplete: PropTypes.string,
  clearTagsAfterCallback: PropTypes.bool,
  disableLengthCheck: PropTypes.bool,
  maxSuggestionsLength: PropTypes.number,
  maxTags: PropTypes.number,
  deliverEmptyInputChange: PropTypes.bool,
  propagateOnClearInput: PropTypes.bool,
  onBeforeChange: PropTypes.func, // (action: string, target: object, tags: object[]) => boolean
};

type Props = PropTypes.InferProps<typeof ReactTagsInput_propTypes> &
  Pick<ReduxState, 'currentOrganization' | 'currentProxyPerson' | 'features'>;

const mapStateToProps = (state: ReduxState) => {
  const { currentOrganization, currentProxyPerson, features } = state;

  return {
    currentOrganization,
    currentProxyPerson,
    features,
  };
};

export default connect(mapStateToProps)(React.memo(ReactTagsInput));
